/**
 * Service pattern grid for weekly schedule selection
 * Allows selecting services for each day of the week
 */

import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  DayOfWeek,
  getDayNameFromNumber,
  type PartialServicePatternEntry,
  type Service,
  type ServiceConflict,
} from "@/types/billing";

// Days of the week (1=Monday...7=Sunday)
const weekDays = [
  { key: DayOfWeek.MONDAY }, // 1
  { key: DayOfWeek.TUESDAY }, // 2
  { key: DayOfWeek.WEDNESDAY }, // 3
  { key: DayOfWeek.THURSDAY }, // 4
  { key: DayOfWeek.FRIDAY }, // 5
  { key: DayOfWeek.SATURDAY }, // 6
  { key: DayOfWeek.SUNDAY }, // 7
];

interface ServicePatternGridProps {
  value: Record<string, PartialServicePatternEntry[]>;
  onChange: (pattern: Record<string, PartialServicePatternEntry[]>) => void;
  // Group types are configured per organisation, so this is the dynamic group
  // key (the same value `isValidServiceSelection` receives), not the legacy
  // two-member `GroupTypeType` union.
  groupType?: string;
  disabled?: boolean;
  services?: Service[]; // Dynamic services from organization settings
  conflicts?: ServiceConflict[]; // Service conflicts for UI blocking
  isValidServiceSelection?: (
    day: string,
    serviceCode: string,
    targetGroupType: string
  ) => boolean;
  globalPattern?: Record<string, Record<string, PartialServicePatternEntry[]>>;
  contractedServices?: Record<string, Set<string>>; // Services from contract (for green highlighting)
}

export function ServicePatternGrid({
  value,
  onChange,
  groupType,
  disabled = false,
  services = [],
  conflicts = [],
  isValidServiceSelection,
  globalPattern,
  contractedServices,
}: ServicePatternGridProps) {
  const { i18n } = useLingui();

  // Helper to convert time string to minutes for sorting
  const timeToMinutes = (time: string): number => {
    const trimmed = (time || '').trim();
    const parts = trimmed.split(':');
    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);

    // Return 0 if any parsed value is not finite
    if (!isFinite(hours) || !isFinite(minutes)) {
      return 0;
    }

    return hours * 60 + minutes;
  };

  // Prepare service slots from dynamic services - sorted by time
  const serviceSlots = services
    .filter((s) => s.active !== false)
    .sort((a, b) => {
      // Sort by time_from first, then by time_to
      const aFromMinutes = timeToMinutes(a.time_from || '00:00');
      const bFromMinutes = timeToMinutes(b.time_from || '00:00');

      if (aFromMinutes !== bFromMinutes) {
        return aFromMinutes - bFromMinutes;
      }

      // If time_from is the same, sort by time_to (descending - longer services first)
      const aToMinutes = timeToMinutes(a.time_to || '23:59');
      const bToMinutes = timeToMinutes(b.time_to || '23:59');
      return bToMinutes - aToMinutes;
    })
    .map((service) => ({
      code: service.code,
      label:
        (service[`name_${i18n.locale}` as keyof Service] as string) ||
        service.name,
      times: `${service.time_from} - ${service.time_to}`,
    }));

  // Check if a service is selected for a day
  const isServiceSelected = (day: string, serviceCode: string): boolean => {
    const dayServices = value[day] || [];
    return dayServices.some((service) => service.serviceCode === serviceCode);
  };

  // Check if a service selection would be invalid due to conflicts
  const isServiceBlocked = (day: string, serviceCode: string): boolean => {
    if (!isValidServiceSelection || !groupType) return false;
    return !isValidServiceSelection(day, serviceCode, groupType);
  };

  // Check if a service is from the contract (for green highlighting)
  const isContractedService = (day: string, serviceCode: string): boolean => {
    if (!contractedServices) return false;
    return contractedServices[day]?.has(serviceCode) || false;
  };

  // Get conflict info for a service on a specific day
  const getServiceConflictInfo = (
    day: string,
    serviceCode: string
  ): ServiceConflict | undefined => {
    return conflicts.find(
      (conflict) => conflict.day === day && conflict.serviceCode === serviceCode
    );
  };

  // Toggle a service for a day
  const toggleService = (day: string, serviceCode: string) => {
    if (disabled) return;

    const isSelected = isServiceSelected(day, serviceCode);
    const isBlocked = isServiceBlocked(day, serviceCode);

    // If trying to select a blocked service, don't allow it
    if (!isSelected && isBlocked) {
      return;
    }

    const currentPattern = { ...value };
    const dayServices = [...(value[day] || [])];

    const serviceIndex = dayServices.findIndex(
      (s) => s.serviceCode === serviceCode
    );

    if (serviceIndex >= 0) {
      // Remove service
      dayServices.splice(serviceIndex, 1);
    } else {
      // Add service
      dayServices.push({
        serviceCode,
        startTime: getServiceStartTime(serviceCode),
        endTime: getServiceEndTime(serviceCode),
        code: serviceCode, // Add code field for compatibility
      });
    }

    // Update or remove day entry
    if (dayServices.length > 0) {
      currentPattern[day] = dayServices;
    } else {
      delete currentPattern[day];
    }

    onChange(currentPattern);
  };

  // Get default start time for a service
  const getServiceStartTime = (serviceCode: string): string => {
    const service = services.find((s) => s.code === serviceCode);
    return service?.time_from || "08:00";
  };

  // Get default end time for a service
  const getServiceEndTime = (serviceCode: string): string => {
    const service = services.find((s) => s.code === serviceCode);
    return service?.time_to || "17:00";
  };

  // Get day label
  const getDayLabel = (day: number) => {
    switch (day) {
      case 1: // DayOfWeek.MONDAY
        return i18n._({ id: "common.days.monday", message: "Monday" });
      case 2: // DayOfWeek.TUESDAY
        return i18n._({ id: "common.days.tuesday", message: "Tuesday" });
      case 3: // DayOfWeek.WEDNESDAY
        return i18n._({ id: "common.days.wednesday", message: "Wednesday" });
      case 4: // DayOfWeek.THURSDAY
        return i18n._({ id: "common.days.thursday", message: "Thursday" });
      case 5: // DayOfWeek.FRIDAY
        return i18n._({ id: "common.days.friday", message: "Friday" });
      case 6: // DayOfWeek.SATURDAY
        return i18n._({ id: "common.days.saturday", message: "Saturday" });
      case 7: // DayOfWeek.SUNDAY
        return i18n._({ id: "common.days.sunday", message: "Sunday" });
      default:
        return String(day);
    }
  };

  // Get day short label
  const getDayShortLabel = (day: number) => {
    switch (day) {
      case 1: // DayOfWeek.MONDAY
        return i18n._({ id: "common.days.short.monday", message: "Mon" });
      case 2: // DayOfWeek.TUESDAY
        return i18n._({ id: "common.days.short.tuesday", message: "Tue" });
      case 3: // DayOfWeek.WEDNESDAY
        return i18n._({ id: "common.days.short.wednesday", message: "Wed" });
      case 4: // DayOfWeek.THURSDAY
        return i18n._({ id: "common.days.short.thursday", message: "Thu" });
      case 5: // DayOfWeek.FRIDAY
        return i18n._({ id: "common.days.short.friday", message: "Fri" });
      case 6: // DayOfWeek.SATURDAY
        return i18n._({ id: "common.days.short.saturday", message: "Sat" });
      case 7: // DayOfWeek.SUNDAY
        return i18n._({ id: "common.days.short.sunday", message: "Sun" });
      default:
        return String(day);
    }
  };

  // Toggle all services for a day
  const toggleDay = (day: string) => {
    if (disabled) return;

    const currentPattern = { ...value };
    const dayServices = currentPattern[day] || [];

    const availableSlots = serviceSlots.filter((slot) => {
      const blocked = isServiceBlocked(day, slot.code);
      const alreadySelected = isServiceSelected(day, slot.code);
      return alreadySelected || !blocked;
    });

    const selectedCount = availableSlots.filter((slot) =>
      isServiceSelected(day, slot.code)
    ).length;

    const shouldClearDay =
      availableSlots.length > 0 && selectedCount === availableSlots.length;

    if (shouldClearDay) {
      if (dayServices.length > 0) {
        delete currentPattern[day];
      }
    } else {
      const newServices = dayServices.filter((service) =>
        availableSlots.some((slot) => slot.code === service.serviceCode)
      );

      availableSlots.forEach((slot) => {
        const alreadySelected = isServiceSelected(day, slot.code);
        const blocked = isServiceBlocked(day, slot.code);
        if (!alreadySelected && !blocked) {
          newServices.push({
            serviceCode: slot.code,
            startTime: getServiceStartTime(slot.code),
            endTime: getServiceEndTime(slot.code),
            code: slot.code,
          });
        }
      });

      if (newServices.length > 0) {
        currentPattern[day] = newServices;
      } else {
        delete currentPattern[day];
      }
    }

    onChange(currentPattern);
  };

  // Toggle a service for weekdays (Monday-Friday)
  const toggleServiceColumn = (serviceCode: string) => {
    if (disabled) return;

    const currentPattern = { ...value };

    const weekdayKeys = weekDays
      .map((day) => day.key)
      .filter(
        (key) => key !== DayOfWeek.SATURDAY && key !== DayOfWeek.SUNDAY
      );
    const availableDays = weekdayKeys.filter((dayKey) => {
      const dayName = getDayNameFromNumber(dayKey);
      const blocked = isServiceBlocked(dayName, serviceCode);
      const alreadySelected = isServiceSelected(dayName, serviceCode);
      return alreadySelected || !blocked;
    });

    const allAvailableSelected =
      availableDays.length > 0 &&
      availableDays.every((dayKey) =>
        isServiceSelected(getDayNameFromNumber(dayKey), serviceCode)
      );

    weekdayKeys.forEach((dayKey) => {
      const dayName = getDayNameFromNumber(dayKey);
      const dayServices = [...(value[dayName] || [])];
      const serviceIndex = dayServices.findIndex(
        (s) => s.serviceCode === serviceCode
      );

      if (allAvailableSelected) {
        if (serviceIndex >= 0) {
          dayServices.splice(serviceIndex, 1);
        }
      } else {
        const blocked = isServiceBlocked(dayName, serviceCode);
        if (serviceIndex < 0 && !blocked) {
          dayServices.push({
            serviceCode,
            startTime: getServiceStartTime(serviceCode),
            endTime: getServiceEndTime(serviceCode),
            code: serviceCode,
          });
        }
      }

      if (dayServices.length > 0) {
        currentPattern[dayName] = dayServices;
      } else {
        delete currentPattern[dayName];
      }
    });

    onChange(currentPattern);
  };

  const overlapInfo = useMemo(() => {
    const cellKeys = new Set<string>();
    const serviceKeys = new Set<string>();

    const parseToMinutes = (time?: string) => {
      if (!time) return NaN;
      const [hours, minutes] = time.split(":").map((part) => Number.parseInt(part, 10));
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return NaN;
      }
      return hours * 60 + minutes;
    };

    const serviceTimeCache = new Map<string, { start: number; end: number }>();
    services.forEach((service) => {
      const start = parseToMinutes(service.time_from);
      const end = parseToMinutes(service.time_to);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        serviceTimeCache.set(service.code, { start, end });
      }
    });

    const allSelections = globalPattern || { [groupType || ""]: value };

    const perDaySelections: Record<
      string,
      Array<{
        serviceCode: string;
        start: number;
        end: number;
        listKey: string;
      }>
    > = {};

    Object.entries(allSelections).forEach(([groupKey, groupPattern]) => {
      if (!groupPattern) return;

      Object.entries(groupPattern).forEach(([dayKey, dayServices]) => {
        if (!dayServices || dayServices.length === 0) return;

        dayServices.forEach((service) => {
          // Entries parsed out of a stored pattern can be missing their code.
          // Without one the listKey below is unmatchable and the entry cannot
          // participate in overlap detection anyway, so skip it.
          if (!service.serviceCode) return;

          const cache = serviceTimeCache.get(service.serviceCode);
          const start = Number.isFinite(parseToMinutes(service.startTime))
            ? parseToMinutes(service.startTime)
            : cache?.start ?? NaN;
          const end = Number.isFinite(parseToMinutes(service.endTime))
            ? parseToMinutes(service.endTime)
            : cache?.end ?? NaN;

          if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
            return;
          }

          const listKey = `${groupKey}-${service.serviceCode}`;

          if (!perDaySelections[dayKey]) {
            perDaySelections[dayKey] = [];
          }

          perDaySelections[dayKey].push({
            serviceCode: service.serviceCode,
            start,
            end,
            listKey,
          });
        });
      });
    });

    Object.entries(perDaySelections).forEach(([dayKey, selections]) => {
      for (let i = 0; i < selections.length; i += 1) {
        for (let j = i + 1; j < selections.length; j += 1) {
          const first = selections[i];
          const second = selections[j];

          const overlaps =
            (first.start >= second.start && first.start < second.end) ||
            (first.end > second.start && first.end <= second.end) ||
            (first.start <= second.start && first.end >= second.end);

          if (!overlaps) continue;

          if (first.listKey === second.listKey) {
            continue;
          }

          if (first.listKey.startsWith(`${groupType}-`)) {
            serviceKeys.add(first.serviceCode);
          }
          if (second.listKey.startsWith(`${groupType}-`)) {
            serviceKeys.add(second.serviceCode);
          }

          if (first.listKey.startsWith(`${groupType}-`)) {
            cellKeys.add(`${dayKey}-${first.serviceCode}`);
          }
          if (second.listKey.startsWith(`${groupType}-`)) {
            cellKeys.add(`${dayKey}-${second.serviceCode}`);
          }
        }
      }
    });

    return { cellKeys, serviceKeys } as const;
  }, [value, services, globalPattern, groupType]);

  // Count selected services
  const totalServices = Object.values(value).reduce(
    (sum, services) => sum + services.length,
    0
  );

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            <Trans>Total selected: {totalServices} services</Trans>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange({})}
              disabled={disabled || totalServices === 0}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
            >
              <Trans>Clear All</Trans>
            </button>
          </div>
        </div>

        {/* Grid */}
        <Card className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full table-layout-auto">
              <thead>
                <tr>
                  <th scope="col" className="text-left p-2 text-sm font-medium w-1/4 min-w-[150px]">
                    <Trans>Service</Trans>
                  </th>
                  {weekDays.map((day) => {
                    const shortLabel = getDayShortLabel(day.key);
                    const fullLabel = getDayLabel(day.key);
                    const dayName = getDayNameFromNumber(day.key);

                    const dayButton = (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleDay(dayName)}
                        disabled={disabled}
                        className="h-auto px-3 py-2 w-full"
                      >
                        <span className="text-sm font-medium">{shortLabel}</span>
                      </Button>
                    );

                    return (
                      <th
                        key={day.key}
                        scope="col"
                        className="text-center p-2 align-middle w-20"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>{dayButton}</TooltipTrigger>
                          <TooltipContent>
                            <span className="text-sm font-medium">
                              {fullLabel}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {serviceSlots.map((slot) => {
                  const isRowOverlap = overlapInfo.serviceKeys.has(slot.code);

                  return (
                    <tr key={slot.code} className="border-t">
                      <th scope="row" className="p-2 align-top w-1/4 min-w-[150px] font-normal">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleServiceColumn(slot.code)}
                          disabled={disabled}
                          className={cn(
                            "h-auto px-3 py-2 w-full flex items-center justify-between gap-2",
                            isRowOverlap && "border-destructive/60 text-destructive"
                          )}
                        >
                          <span className="text-sm font-medium truncate flex-1 text-left min-w-0">
                            {i18n._(slot.label)}
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {slot.times}
                          </span>
                        </Button>
                      </th>
                      {weekDays.map((day) => {
                        const dayName = getDayNameFromNumber(day.key);
                        const isSelected = isServiceSelected(
                          dayName,
                          slot.code
                        );
                        const isBlocked = isServiceBlocked(dayName, slot.code);
                        const conflictInfo = getServiceConflictInfo(
                          dayName,
                          slot.code
                        );
                        const isCheckboxDisabled =
                          disabled || (!isSelected && isBlocked);

                        const isOverlapCell =
                          isSelected &&
                          overlapInfo.cellKeys.has(
                            `${dayName}-${slot.code}`
                          );

                        const isContracted = isContractedService(
                          dayName,
                          slot.code
                        );

                        const checkboxElement = (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() =>
                              toggleService(dayName, slot.code)
                            }
                            disabled={isCheckboxDisabled}
                            className={cn(
                              "h-5 w-5",
                              isBlocked &&
                                !isSelected &&
                                "opacity-50 cursor-not-allowed"
                            )}
                          />
                        );

                        return (
                      <td
                        key={`${day.key}-${slot.code}`}
                        className={cn(
                          "text-center p-2 w-20",
                          (isOverlapCell || isContracted) && "relative"
                        )}
                      >
                        {/* Tinted background for contracted services. The
                            token is opaque, and an absolutely positioned box
                            paints above the static label that follows it, so
                            the label below is positioned too — otherwise the
                            tint hides the checkbox and its checked state. */}
                        {isContracted && !isOverlapCell && (
                          <div className="absolute inset-2 rounded bg-success-muted pointer-events-none" />
                        )}

                        {/* Red background for overlaps (takes priority) */}
                        {isOverlapCell && (
                          <div className="absolute inset-2 rounded border border-destructive/60 bg-destructive/10 pointer-events-none" />
                        )}

                        {isBlocked && !isSelected && conflictInfo ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <label className="relative flex items-center justify-center cursor-not-allowed">
                                {checkboxElement}
                              </label>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">
                                    <Trans>
                                      {conflictInfo.serviceName} is already
                                      selected in{" "}
                                      {conflictInfo.conflictingGroups.join(
                                        ", "
                                      )}
                                    </Trans>
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <label
                                className={cn(
                                  "relative flex items-center justify-center",
                                  isCheckboxDisabled
                                    ? "cursor-not-allowed"
                                    : "cursor-pointer"
                                )}
                              >
                                {checkboxElement}
                              </label>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

      </div>
    </TooltipProvider>
  );
}
